import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Line, LineChart } from "recharts";
import { ArrowDownRight, ArrowUpRight, Loader2, Minus } from "lucide-react";
import { api } from "@/lib/api";

/* ============================================================================
   Propojo Ops Console design system
   Stripe precision (dense tables, mono numerics, crisp 1px borders, sparklines)
   + Shopify warmth (rounded cards, big readable numbers, emerald accent).
   Light-first with a dark toggle. Emerald/slate so it never reads as the
   indigo customer product.
   ========================================================================== */

export const T = {
  page: "min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50",
  surface: "bg-white dark:bg-slate-900",
  card: "rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm",
  border: "border-slate-200 dark:border-slate-800",
  muted: "text-slate-500 dark:text-slate-400",
  h1: "text-xl sm:text-2xl font-semibold tracking-tight",
  h2: "text-base md:text-lg font-medium tracking-tight",
  label: "text-[11px] font-medium uppercase tracking-[0.09em] text-slate-500 dark:text-slate-400",
  num: "font-num tabular-nums",
  th: "text-[11px] font-medium uppercase tracking-[0.07em] text-slate-500 dark:text-slate-400 text-left px-4 py-2.5 bg-slate-50/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 sticky top-0 backdrop-blur",
  td: "px-4 py-2.5 border-b border-slate-100 dark:border-slate-800/70 text-sm text-slate-700 dark:text-slate-300 align-middle",
  row: "hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors",
  input:
    "rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-colors",
  btn: "inline-flex items-center justify-center gap-1.5 rounded-md text-[13px] font-medium px-3 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
  btnPrimary: "bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500 dark:text-slate-950 dark:hover:bg-emerald-400",
  btnGhost:
    "border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800",
  btnQuiet: "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800",
  btnDanger:
    "border border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40",
};

/* ---------------------------------- theme --------------------------------- */

const ThemeCtx = createContext({ dark: false, toggle: () => {} });
export const useOpsTheme = () => useContext(ThemeCtx);

export function OpsThemeProvider({ children }) {
  const [dark, setDark] = useState(() => localStorage.getItem("pj_ops_theme") === "dark");
  useEffect(() => {
    localStorage.setItem("pj_ops_theme", dark ? "dark" : "light");
  }, [dark]);
  return (
    <ThemeCtx.Provider value={{ dark, toggle: () => setDark((v) => !v) }}>
      <div className={dark ? "dark" : ""}>{children}</div>
    </ThemeCtx.Provider>
  );
}

/* ------------------------------- admin session ---------------------------- */

const AdminCtx = createContext(null);
export const useAdmin = () => useContext(AdminCtx);

export function AdminProvider({ children }) {
  const [me, setMe] = useState(null);
  const [state, setState] = useState("loading");
  useEffect(() => {
    api
      .get("/admin/me")
      .then((r) => {
        setMe(r.data);
        setState("ok");
      })
      .catch(() => setState("denied"));
  }, []);
  const can = (perm) => !!me && (me.permissions.includes("*") || me.permissions.includes(perm));
  return <AdminCtx.Provider value={{ me, state, can }}>{children}</AdminCtx.Provider>;
}

/* --------------------------------- polling -------------------------------- */

export function usePoll(fn, ms, deps = []) {
  const [tick, setTick] = useState(0);
  const run = useCallback(fn, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    run();
    const t = setInterval(() => setTick((v) => v + 1), ms);
    return () => clearInterval(t);
  }, [run, ms]);
  useEffect(() => {
    if (tick) run();
  }, [tick, run]);
}

/* ------------------------------- primitives ------------------------------- */

export const Card = ({ title, hint, actions, children, className = "", testid, pad = false }) => (
  <section data-testid={testid} className={`${T.card} overflow-hidden ${className}`}>
    {(title || actions) && (
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
        <div className="min-w-0">
          <h2 className="text-[13px] font-semibold text-slate-900 dark:text-slate-100 truncate">{title}</h2>
          {hint && <p className={`text-xs mt-0.5 ${T.muted} truncate`}>{hint}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </header>
    )}
    <div className={pad ? "p-4" : ""}>{children}</div>
  </section>
);

export const Delta = ({ delta, suffix = "vs prev 7d", testid }) => {
  if (!delta) return null;
  const up = delta.trend === "up";
  const flat = delta.trend === "flat";
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const tone = flat
    ? "text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-400"
    : up
      ? "text-emerald-700 bg-emerald-50 dark:bg-emerald-500/10 dark:text-emerald-400"
      : "text-rose-700 bg-rose-50 dark:bg-rose-500/10 dark:text-rose-400";
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5">
      <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>
        <Icon className="w-3 h-3" />
        {Math.abs(delta.change)}%
      </span>
      <span className="text-[11px] text-slate-400 dark:text-slate-500">{suffix}</span>
    </span>
  );
};

export const KPI = ({ label, value, delta, spark, dataKey, tone = "slate", testid, footer }) => {
  const stroke = delta?.trend === "down" ? "#f43f5e" : "#10b981";
  const valueTone = { slate: "", emerald: "text-emerald-600 dark:text-emerald-400", rose: "text-rose-600 dark:text-rose-400" }[tone];
  return (
    <div data-testid={testid} className={`${T.card} p-4 flex flex-col gap-3`}>
      <p className={T.label}>{label}</p>
      <p className={`text-3xl leading-none font-semibold tracking-tight ${T.num} ${valueTone}`}>{value}</p>
      <div className="flex items-end justify-between gap-3 mt-auto">
        <div className="min-w-0">{delta ? <Delta delta={delta} /> : footer ? <span className={`text-[11px] ${T.muted}`}>{footer}</span> : null}</div>
        {spark?.length > 1 && (
          <div className="w-[86px] h-8 shrink-0">
            <LineChart data={spark} width={86} height={32}>
              <Line type="monotone" dataKey={dataKey} stroke={stroke} strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </div>
        )}
      </div>
    </div>
  );
};

const PILL = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 ring-emerald-600/20",
  paid: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 ring-emerald-600/20",
  accepted: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 ring-emerald-600/20",
  lifetime: "bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300 ring-violet-600/20",
  trialing: "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300 ring-blue-600/20",
  sent: "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300 ring-blue-600/20",
  viewed: "bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300 ring-sky-600/20",
  past_due: "bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300 ring-amber-600/20",
  created: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 ring-slate-500/20",
  draft: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 ring-slate-500/20",
  expired: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 ring-rose-600/20",
  cancelled: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 ring-rose-600/20",
  failed: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 ring-rose-600/20",
  declined: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 ring-rose-600/20",
  disabled: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 ring-rose-600/20",
};

export const Pill = ({ value, pulse = false, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset whitespace-nowrap ${
      PILL[String(value)] || "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 ring-slate-500/20"
    }`}
  >
    {pulse && <span className="relative flex w-1.5 h-1.5"><span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-75 animate-ping" /><span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500" /></span>}
    {String(value ?? "—").replace(/_/g, " ")}
  </span>
);

export const Btn = ({ variant = "ghost", className = "", children, ...rest }) => {
  const map = { primary: T.btnPrimary, ghost: T.btnGhost, quiet: T.btnQuiet, danger: T.btnDanger };
  return (
    <button className={`${T.btn} ${map[variant]} ${className}`} {...rest}>
      {children}
    </button>
  );
};

export const Loading = ({ label = "Loading" }) => (
  <div className="py-16 grid place-items-center gap-2">
    <Loader2 className="w-5 h-5 animate-spin text-emerald-600 dark:text-emerald-400" />
    <span className={`text-xs ${T.muted}`}>{label}…</span>
  </div>
);

export const Empty = ({ label, hint, icon: Icon }) => (
  <div className="py-16 text-center px-6">
    {Icon && (
      <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 grid place-items-center mx-auto mb-3">
        <Icon className="w-5 h-5 text-slate-400" />
      </div>
    )}
    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</p>
    {hint && <p className={`text-xs mt-1 ${T.muted}`}>{hint}</p>}
  </div>
);

export const Pager = ({ page, pages, total, onPage, testid = "pager" }) => (
  <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-800">
    <span className={`text-xs ${T.muted}`} data-testid={`${testid}-total`}>
      <span className={T.num}>{total}</span> records · page <span className={T.num}>{page}</span> of{" "}
      <span className={T.num}>{pages}</span>
    </span>
    <div className="flex gap-2">
      <Btn data-testid={`${testid}-prev`} disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Previous
      </Btn>
      <Btn data-testid={`${testid}-next`} disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next
      </Btn>
    </div>
  </div>
);

export const Bars = ({ rows, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <ul className="p-4 space-y-2.5" data-testid={testid}>
      {rows.map((r) => (
        <li key={r.name}>
          <div className="flex items-baseline justify-between gap-3 mb-1">
            <span className="text-[13px] text-slate-700 dark:text-slate-300 truncate">{r.name}</span>
            <span className={`text-xs ${T.num} ${T.muted}`}>{r.count}</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${(r.count / max) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};

/* --------------------------------- charts --------------------------------- */

export const chartTip = (dark) => ({
  contentStyle: {
    background: dark ? "#0f172a" : "#ffffff",
    border: `1px solid ${dark ? "#1e293b" : "#e2e8f0"}`,
    borderRadius: 8,
    boxShadow: "0 10px 24px -12px rgba(15,23,42,0.35)",
    fontSize: 12,
    padding: "8px 10px",
  },
  labelStyle: { color: dark ? "#94a3b8" : "#64748b", fontWeight: 500, marginBottom: 4 },
  itemStyle: { color: dark ? "#e2e8f0" : "#0f172a" },
});

export const axis = (dark) => ({
  tick: { fontSize: 11, fill: dark ? "#64748b" : "#94a3b8", fontFamily: "JetBrains Mono" },
  axisLine: false,
  tickLine: false,
});

export const gridStroke = (dark) => (dark ? "rgba(148,163,184,0.12)" : "rgba(100,116,139,0.14)");

/* --------------------------------- format -------------------------------- */

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export const fmtDay = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export const ago = (seconds) => {
  if (seconds == null) return "—";
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

export const dur = (seconds) => {
  if (!seconds) return "0s";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  return m < 60 ? `${m}m ${seconds % 60}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

export const flag = (code) =>
  code && code.length === 2
    ? String.fromCodePoint(...[...code.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)))
    : "";
